import React, { useEffect, useState } from 'react';
import { Table, Button, Stack, IconButton, SelectPicker, Message, Form, Panel } from 'rsuite';
import TrashIcon from '@rsuite/icons/Trash';
import { AxiosError } from 'axios';
import PageContent from '@/components/PageContent';
import ErrorMessage from '@/components/ErrorMessage';
import FetchingSelect from '@/components/FetchingSelect';
import { BaseErrorModel } from '@/models/core.model';
import { PointTypeAiStyle, UpsertPointTypeAiStyle } from '@/models/point-type.model';
import { AdvanceShareStyleCategoryList } from '@/models/advance-share-style.model';
import { AdvanceShareStyleTypeList } from '@/models/advance-share-style-type.model';
import { addPointTypeAiStyle, deletePointTypeAiStyle, getPointTypeAiStyles } from '@/services/point-type.service';
import { getAdvanceShareStylesLookup } from '@/services/advance-share-style.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';

const { Column, HeaderCell, Cell } = Table;

type RestrictionKind = 'styleType' | 'styleId' | 'category';
const kindList = [
  { value: 'styleType', label: 'Code style (StyleType)' },
  { value: 'styleId', label: 'DB style (StyleId)' },
  { value: 'category', label: 'Style category' },
];

interface Props {
  pointTypeId: number;
}

const PointTypeAiStyleList: React.FC<Props> = ({ pointTypeId }) => {
  const [items, setItems] = useState<PointTypeAiStyle[]>([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState<RestrictionKind>('styleType');
  const [value, setValue] = useState<number | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);

  useEffect(() => {
    fetchData();
  }, [pointTypeId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getPointTypeAiStyles(pointTypeId);
      if (result.success) setItems(result.result ?? []);
    } catch (err) {
      setItems([]);
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching AI style restrictions: ${em?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (value == undefined) return;
    const body: UpsertPointTypeAiStyle = { [kind]: value };
    setErrorModel(undefined);
    setSaving(true);
    try {
      const result = await addPointTypeAiStyle(pointTypeId, body);
      if (result.success) {
        toastSuccessMessage('Restriction added');
        setValue(undefined);
        fetchData();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: PointTypeAiStyle) => {
    if (!window.confirm('Remove this restriction?')) return;
    try {
      await deletePointTypeAiStyle(pointTypeId, row.id);
      toastSuccessMessage('Restriction removed');
      fetchData();
    } catch (err) {
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in removing restriction: ${em?.errorMessage}`);
    }
  };

  const describe = (row: PointTypeAiStyle) => {
    if (row.styleType != null) return ['Code style', AdvanceShareStyleTypeList.find(s => s.value == row.styleType)?.label ?? `#${row.styleType}`];
    if (row.styleId != null) return ['DB style', `Style #${row.styleId}`];
    if (row.category != null) return ['Category', AdvanceShareStyleCategoryList.find(c => c.value == row.category)?.label ?? `#${row.category}`];
    return ['-', '-'];
  };

  return (
    <PageContent>
      <Message type="info" showIcon style={{ marginBottom: 16 }}>
        Controls which AI image styles this point type can pay for. <b>No rows = the point type pays for all styles.</b> Once at
        least one row exists, only matching styles (by code style, DB style, or category) can be paid with it.
      </Message>

      <Panel bordered header="Add restriction" style={{ marginBottom: 16 }}>
        <Form layout="inline">
          <Form.Group>
            <SelectPicker
              data={kindList}
              searchable={false}
              cleanable={false}
              value={kind}
              onChange={val => { setKind((val ?? 'styleType') as RestrictionKind); setValue(undefined); }}
              style={{ width: 220 }}
            />
          </Form.Group>
          <Form.Group>
            {kind == 'styleType' && (
              <SelectPicker data={AdvanceShareStyleTypeList} value={value ?? null} onChange={val => setValue(val ?? undefined)}
                placeholder="Select code style" style={{ width: 280 }} />
            )}
            {kind == 'styleId' && (
              <FetchingSelect fetchData={(text, page) => getAdvanceShareStylesLookup(text, page)} value={value}
                onChange={val => setValue(val ?? undefined)} placeholder="Select DB style" style={{ width: 280 }} />
            )}
            {kind == 'category' && (
              <SelectPicker data={AdvanceShareStyleCategoryList} searchable={false} value={value ?? null}
                onChange={val => setValue(val ?? undefined)} placeholder="Select category" style={{ width: 280 }} />
            )}
          </Form.Group>
          <Button appearance="primary" onClick={handleAdd} loading={saving} disabled={value == undefined}>Add</Button>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 12 }} />}
      </Panel>

      <Table autoHeight data={items} loading={loading}>
        <Column flexGrow={1}>
          <HeaderCell>Kind</HeaderCell>
          <Cell>{(row: PointTypeAiStyle) => describe(row)[0]}</Cell>
        </Column>
        <Column flexGrow={3}>
          <HeaderCell>Value</HeaderCell>
          <Cell>{(row: PointTypeAiStyle) => describe(row)[1]}</Cell>
        </Column>
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(row: PointTypeAiStyle) => (
              <IconButton icon={<TrashIcon />} circle size="sm" color="red" appearance="ghost" onClick={() => handleDelete(row)} />
            )}
          </Cell>
        </Column>
      </Table>
      {!loading && items.length == 0 && (
        <Stack justifyContent="center" style={{ padding: 12, color: '#888' }}>No restrictions — pays for all styles.</Stack>
      )}
    </PageContent>
  );
};

export default PointTypeAiStyleList;
